import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

// Interfaces
import { Participant } from "../../../../interfaces/Participant";

// Custom components
import ParticipantDialog from "./ParticipantDialog";
import SearchBar from "../../../general/SearchBar";

// Hooks
import { useFetchUser } from "../../../../hooks/useFetchUser";

// Settings
import settings from "../../../../settings.json";

// Firebase
import { firestore } from "../../../../firebase/firebase";
import {
    collection,
    query,
    orderBy,
    doc,
    getDocs,
    getCountFromServer,
    startAfter,
    limit,
    QueryDocumentSnapshot,
    DocumentData,
    updateDoc,
} from "firebase/firestore";
import { genDoc } from "../../../../utils/firebase";

// Mui
import { Button, Grid, Switch } from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";

const COLLECTION = "Participants";

const Participants: React.FC = () => {
    const hist = useHistory();
    const { currentAdmin, setLoading } = useFetchUser();
    const { enqueueSnackbar } = useSnackbar();

    const [participants, setParticipants] = useState<Participant[]>([]);
    const [filteredItems, setFilteredItems] = useState<Participant[]>([]);
    const [count, setCount] = useState<number>(0);
    const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);

    // Cache of all participants loaded for search — fetched once on first search, reused after
    const allParticipantsCache = useRef<Participant[] | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyFilter = (items: Participant[], q: string) => {
        const lower = q.toLowerCase().trim();
        return items
            .filter(x => {
                if (!x.firstName || !x.participantId || !x.email || !x.createdAt) return false;
                return (
                    x.email.toLowerCase().includes(lower) ||
                    x.firstName.toLowerCase().includes(lower) ||
                    x.participantId.toLowerCase().includes(lower) ||
                    x.sex.toLowerCase().includes(lower) ||
                    moment(x.createdAt).format("DD-MM-YYYY HH:mm:ss").includes(lower)
                );
            })
            .sort((a, b) => (a.createdAt && b.createdAt ? a.createdAt.getTime() - b.createdAt.getTime() : -1));
    };

    const fetchFirstPage = async () => {
        try {
            const localQuery = query(
                collection(firestore, COLLECTION),
                orderBy("email", "asc"),
                limit(settings.page.rowsPerPage)
            );
            const snapshot = await getDocs(localQuery);
            const data = snapshot.docs.map(genDoc<Participant>()).filter((item: Participant) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setLastVisible(snapshot.docs[snapshot.docs.length - 1] ?? null);
            setParticipants(data);
            setFilteredItems(data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchCount = async () => {
        try {
            const snapshot = await getCountFromServer(collection(firestore, COLLECTION));
            setCount(snapshot.data().count);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchMoreItems = async () => {
        if (!lastVisible) return;
        try {
            const localQuery = query(
                collection(firestore, COLLECTION),
                orderBy("email", "asc"),
                limit(settings.page.rowsPerPage),
                startAfter(lastVisible)
            );
            const snapshot = await getDocs(localQuery);
            if (snapshot.docs.length === 0) return;
            const data = snapshot.docs.map(genDoc<Participant>()).filter((item: Participant) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setParticipants(prev => [...prev, ...data]);
            setFilteredItems(prev => [...prev, ...data]);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSearch = (value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setSearchQuery(value), 300);
    };

    const filterItems = async (q: string) => {
        // Populate cache once; reuse on subsequent searches
        if (!allParticipantsCache.current) {
            const snapshot = await getDocs(collection(firestore, COLLECTION));
            allParticipantsCache.current = snapshot.docs.map(genDoc<Participant>());
        }
        const filtered = applyFilter(allParticipantsCache.current, q);
        setCount(filtered.length);
        setFilteredItems(filtered);
    };

    const handleDelete = async (p: Participant) => {
        try {
            if (currentAdmin && currentAdmin.roles && currentAdmin.roles.includes(settings.app.highestRole)) {
                if (p.id) {
                    await updateDoc(doc(firestore, COLLECTION, p.id), { isDeleted: !p.isDeleted });
                    // Update both local lists and the cache
                    const toggle = (x: Participant) => x.id === p.id ? { ...x, isDeleted: !p.isDeleted } : x;
                    setParticipants(prev => prev.map(toggle));
                    setFilteredItems(prev => prev.map(toggle));
                    if (allParticipantsCache.current) {
                        allParticipantsCache.current = allParticipantsCache.current.map(toggle);
                    }
                }
            } else {
                enqueueSnackbar("You don't have the required role", { variant: "error" });
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (!searchQuery) {
            setFilteredItems(participants);
            fetchCount();
            return;
        }
        filterItems(searchQuery);
    }, [searchQuery]); // eslint-disable-line

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchFirstPage(), fetchCount()]).finally(() => setLoading(false));
    }, []); // eslint-disable-line

    return (
        <>
            <SearchBar onSearch={handleSearch}>
                <div className="searchbar__button">
                    <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                        Add
                    </Button>
                </div>
            </SearchBar>

            <div id="scrollableDiv" className="scrollable__container">
                <InfiniteScroll
                    dataLength={filteredItems.length}
                    next={searchQuery ? () => undefined : fetchMoreItems}
                    hasMore={!searchQuery && filteredItems.length < count}
                    loader={<h4 className="infinitescroll__container__message">Fetching next items...</h4>}
                    endMessage={!filteredItems.length && <h4 className="infinitescroll__container__message">No data</h4>}
                    className="infinitescroll__container"
                    scrollableTarget="scrollableDiv"
                >
                    {filteredItems.map(x => (
                        <div key={x.id} className="infinitescroll__container__card" onDoubleClick={() => hist.push(`participant/${x.id}/bio/0`)}>
                            <Grid item container>
                                <Grid item xs={12} sm={3}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Firstname</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.firstName}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Participant Id</div>
                                            </div>
                                            <div className="centered__parent">
                                                <span className="infinitescroll__container__text">{x.participantId}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Email</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.email}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Sex</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.sex}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Age</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.age}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <div className="centered__parent">
                                        <Switch
                                            checked={!x.isDeleted}
                                            onChange={() => handleDelete(x)}
                                            name="checkedA"
                                            inputProps={{
                                                "aria-label": "secondary checkbox",
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>
            {open && <ParticipantDialog open={open} setOpen={setOpen} />}
        </>
    );
};

export default Participants;
